import React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { ThunkDispatch } from 'redux-thunk';
import { FaChevronDown } from 'react-icons/fa';

import { RootState } from '../../store';
import Order from '../../models/Order';
import { endCollabMode } from '../../store/order/actions';
import {
    isAutoUpdateEnabled,
    isOrderEditableLocally,
    resolveActiveOrderId,
} from '../../utils/orderRouteUtils';
import { IDLE_POLLS_BEFORE_SUSPEND } from '../../utils/orderCollabStorage';

interface StateProps {
    fromOrderId: number | null;
    order: Order | undefined;
    isSyncing: boolean;
    endCollabNonce: number;
    collabActiveNonce: number;
    remoteOrderSyncToken: number;
    remoteOrderUnavailableNonce: number;
    orderNotFound: boolean;
    orderRemotelyUnavailableReason: 'closed' | 'deleted' | 'unavailable' | null;
    userEmail: string | null;
}

interface DispatchProps {
    endCollabMode: () => void;
}

type Props = StateProps & DispatchProps & RouteComponentProps;

interface ComponentState {
    expanded: boolean;
}

class AutoUpdateHeaderStatus extends React.Component<Props, ComponentState> {
    state: ComponentState = {
        expanded: false,
    };

    panelRef = React.createRef<HTMLDivElement>();

    componentDidMount() {
        document.addEventListener('mousedown', this.handleClickOutside);
    }

    componentDidUpdate(prevProps: Props) {
        if (
            prevProps.orderRemotelyUnavailableReason !== this.props.orderRemotelyUnavailableReason ||
            prevProps.orderNotFound !== this.props.orderNotFound
        ) {
            this.setState({ expanded: false });
        }
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
    }

    handleClickOutside = (event: MouseEvent) => {
        if (!this.state.expanded || !this.panelRef.current) {
            return;
        }

        if (!this.panelRef.current.contains(event.target as Node)) {
            this.setState({ expanded: false });
        }
    };

    getOrderId = () =>
        resolveActiveOrderId(
            this.props.location.pathname,
            this.props.fromOrderId
        );

    isAutoUpdateVisible = () => {
        const orderId = this.getOrderId();

        if (!orderId) {
            return false;
        }

        if (this.props.orderNotFound || this.props.orderRemotelyUnavailableReason) {
            return false;
        }

        if (!isOrderEditableLocally(this.props.order)) {
            return false;
        }

        return isAutoUpdateEnabled(orderId, this.props.userEmail);
    };

    toggleExpanded = () => {
        this.setState((prev) => ({ expanded: !prev.expanded }));
    };

    handleStop = () => {
        this.props.endCollabMode();
        this.setState({ expanded: false });
    };

    render() {
        if (!this.isAutoUpdateVisible()) {
            return null;
        }

        const { isSyncing } = this.props;
        const { expanded } = this.state;

        return (
            <div className="header-main__icons__collab-sync" ref={this.panelRef}>
                <div className={`live-instrument${expanded ? ' live-instrument--expanded' : ''}`}>
                    <button
                        type="button"
                        className="live-instrument__trigger"
                        onClick={this.toggleExpanded}
                        aria-expanded={expanded}
                        title="Collaborate on this order — click for details"
                    >
                        <span className={`live-instrument__dot${isSyncing ? ' live-instrument__dot--syncing' : ''}`} />
                        <span className="live-instrument__label">Collaborate</span>
                        <FaChevronDown
                            className={`live-instrument__chevron${expanded ? ' live-instrument__chevron--open' : ''}`}
                            aria-hidden
                        />
                    </button>
                    {expanded && (
                        <div className="live-instrument__panel">
                            <p className="live-instrument__status">
                                {isSyncing
                                    ? 'Updating order from a colleague…'
                                    : 'This order updates automatically when colleagues make changes.'}
                            </p>
                            <p className="live-instrument__hint">
                                Collaboration pauses after {IDLE_POLLS_BEFORE_SUSPEND} checks with no changes. It resumes automatically when someone edits the order again.
                            </p>
                            <button
                                type="button"
                                className="live-instrument__stop"
                                onClick={this.handleStop}
                                disabled={isSyncing}
                            >
                                Stop collaborating
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state: RootState): StateProps => {
    const orderState = state.order.order;
    const order = orderState.order || orderState.dashboardOrder;

    return {
        fromOrderId: order && order.orderheader ? order.orderheader.appOrderId : null,
        order,
        isSyncing: orderState.collabSyncUi.status === 'syncing',
        endCollabNonce: orderState.endCollabNonce,
        collabActiveNonce: orderState.collabActiveNonce,
        remoteOrderSyncToken: orderState.remoteOrderSyncToken,
        remoteOrderUnavailableNonce: orderState.remoteOrderUnavailableNonce,
        orderNotFound: orderState.orderNotFound,
        orderRemotelyUnavailableReason: orderState.orderRemotelyUnavailableReason,
        userEmail: state.user.user.user && state.user.user.user.email
            ? state.user.user.user.email
            : null,
    };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<{}, {}, any>): DispatchProps => ({
    endCollabMode: () => {
        dispatch(endCollabMode());
    },
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(AutoUpdateHeaderStatus));
